import { formatMsDuration } from '@chronosphereio/core';
import { produce } from 'immer';
import { merge } from 'lodash';
import {
  DataFrame,
  Field,
  FieldType,
  Histogram,
  HistogramBucket,
  PrometheusQuerySpec,
  ReducersMap,
  SeriesConfig,
  SingleValue,
  SingleValueTimeSeries,
  TimeSeries,
} from '@/core';
import {
  MatrixData,
  ParsedValueTuple,
  PROM_HISTOGRAM_ERROR_MESSAGE,
  parseSampleValue,
  parseValueTuple,
  ValueTuple,
} from '@/prometheus-plugin/model';
import {
  GetFormattedPrometheusSeriesNameOpts,
  getFormattedPrometheusSeriesName,
  getLabelValue,
} from '@/prometheus-plugin/utils';
import { OffsetLabel, SeriesIdLabel } from '../get-comparable-data';

const DATAFRAME_METRIC_FIELD_NAME = 'Value';

/**
 * Convert Prometheus native histograms into an array of histograms
 */
export function transformNativeHistogramToHeatmap(result: MatrixData['result']) {
  const heatmap: Histogram[] = [];
  const histograms = result[0]?.histograms;
  if (histograms) {
    for (let i = 0; i < histograms.length; i++) {
      const timestamp = (histograms[i]?.[0] ?? 0) * 1000;
      const newBuckets: HistogramBucket[] = [];
      const buckets = histograms[i]?.[1]?.buckets;
      if (buckets) {
        for (let j = 0; j < buckets.length; j++) {
          const lowerBound = parseSampleValue(buckets[j]?.[1] ?? '');
          const upperBound = parseSampleValue(buckets[j]?.[2] ?? '');
          const count = parseSampleValue(buckets[j]?.[3] ?? '');
          newBuckets.push({ count, upperBound, lowerBound });
        }
      }
      heatmap.push({ timestamp, buckets: newBuckets });
    }
  }

  return heatmap;
}

/**
 * Convert Prometheus histograms, where each time series represents a bucket, into an array of histograms.
 */
export function transformTimeSeriesHistogramToHeatmap(result: MatrixData['result']) {
  const bucketLookup = findBuckets(result);
  const datapoints = flattenTimeSeriesHistogramData(result, bucketLookup);
  const histograms = datapointsToHistograms(datapoints);
  decumulateHistogramData(histograms);
  return histograms;
}

type BucketLookup = Record<string, { upperBound: number; lowerBound: number }>;

export const BUCKET_LABEL = 'le';

/**
 * Get bucket definitions from result set and find lower bounds for each bucket
 */
function findBuckets(result: MatrixData['result']) {
  const bucketLookup: BucketLookup = {};
  for (let i = 0; i < result.length; i++) {
    const bucket = result[i];
    if (!bucket) continue;
    const bucketLabelValue = bucket.metric[BUCKET_LABEL];
    if (!bucketLabelValue) {
      throw new Error('Bucket upper bound is missing');
    }
    if (bucketLabelValue in bucketLookup) {
      throw new Error('Found duplicate buckets. Consider aggregating the series.');
    }
    const upperBound = parseSampleValue(bucketLabelValue);
    if (isNaN(upperBound)) {
      throw new Error('Bucket upper bound is not a number');
    }
    bucketLookup[bucketLabelValue] = { upperBound, lowerBound: 0 /* placeholder value */ };
  }

  const sortedBucketKeys = Object.keys(bucketLookup).sort(
    (a, b) => (bucketLookup[a]?.upperBound ?? 0) - (bucketLookup[b]?.upperBound ?? 0)
  );
  for (let i = 0; i < sortedBucketKeys.length; i++) {
    const key = sortedBucketKeys[i];
    if (!key) continue;
    const entry = bucketLookup[key];
    if (!entry) continue;
    if (i > 0) {
      const prevKey = sortedBucketKeys[i - 1];
      if (!prevKey) continue;
      entry.lowerBound = bucketLookup[prevKey]?.upperBound ?? 0;
    } else {
      entry.lowerBound = entry.upperBound > 0 ? 0 : -Infinity;
    }
  }

  return bucketLookup;
}

type HistogramDataPoint = { timestamp: number; upperBound: number; lowerBound: number; count: number };

/**
 * Build a flat array of all datapoints.
 *
 * This allows us to efficiently convert from bucket-oriented data structure to timestamp-oriented
 * structure, while accounting for missing datapoints.
 */
function flattenTimeSeriesHistogramData(result: MatrixData['result'], bucketLookup: BucketLookup) {
  const datapoints: HistogramDataPoint[] = [];
  for (let i = 0; i < result.length; i++) {
    const bucketEntry = result[i];
    if (!bucketEntry) continue;
    const bucketId = bucketEntry.metric[BUCKET_LABEL];
    if (!bucketId) continue;
    const bucket = bucketLookup[bucketId];
    if (!bucket) continue;

    const histogramValues = bucketEntry.values;
    if (!histogramValues) continue;
    for (let j = 0; j < histogramValues.length; j++) {
      const valueTuple = histogramValues[j];
      if (!valueTuple) continue;
      const parsedValueTuple = parseValueTuple(valueTuple);
      const timestamp = parsedValueTuple[0];
      datapoints.push({
        timestamp,
        upperBound: bucket.upperBound,
        lowerBound: bucket.lowerBound,
        count: parsedValueTuple[1],
      });
    }
  }

  // Sort the datapoints first by timestamp, second by upperBound
  datapoints.sort((a, b) => {
    return a.timestamp === b.timestamp ? a.upperBound - b.upperBound : a.timestamp - b.timestamp;
  });

  return datapoints;
}

/**
 * Build an array of histograms based on flat array of datapoints
 */
function datapointsToHistograms(datapoints: HistogramDataPoint[]) {
  const histograms: Histogram[] = [];
  let timestamp = 0;
  let histogram: Histogram = { timestamp, buckets: [] };
  for (let i = 0; i < datapoints.length; i++) {
    const datapoint = datapoints[i];
    if (!datapoint) continue;
    if (datapoint.timestamp !== timestamp) {
      // at the next timestamp, create a new histogram object
      timestamp = datapoint.timestamp;
      histogram = { timestamp, buckets: [] };
      histograms.push(histogram);
    }
    histogram.buckets.push({
      count: datapoint.count,
      upperBound: datapoint.upperBound,
      lowerBound: datapoint.lowerBound,
    });
  }
  return histograms;
}

/**
 * Time series raw data counts are cumulative. We need to subtract values from lower buckets.
 */
function decumulateHistogramData(histograms: Histogram[]) {
  for (let i = 0; i < histograms.length; i++) {
    const histogram = histograms[i];
    if (!histogram) continue;
    for (let j = histogram.buckets.length - 1; j > 0; j--) {
      const bucket = histogram.buckets[j];
      if (!bucket) continue;
      const bucketCount = bucket.count ?? 0;
      const lowerBucketCount = histogram.buckets[j - 1]?.count ?? 0;
      bucket.count = bucketCount - lowerBucketCount;
    }
  }
}

type ParsePrometheusTimeSeriesOpts = Omit<GetFormattedPrometheusSeriesNameOpts, 'metric'> & {
  series: MatrixData['result'][0];
};

type TransformPromRangeQueryToTimeSeriesOpts = Omit<ParsePrometheusTimeSeriesOpts, 'series'> & {
  /**
   * The raw query with the global variables preserved.
   */
  queryWithGlobalVariables?: string;
  result: MatrixData['result'];
  seriesConfig?: (series: MatrixData['result'][number]) => SeriesConfig;
};

/**
 * Transforms Prometheus query result into TimeSeries[]
 */
export function transformPromRangeQueryToTimeSeries({
  result,
  query,
  queryWithGlobalVariables,
  seriesName,
  seriesNameFormat,
  alias,
  seriesConfig: getSeriesConfig,
}: TransformPromRangeQueryToTimeSeriesOpts): TimeSeries[] {
  const defaultSeriesConfig = () => {
    return {
      meta: {
        // We want to keep global variables so we can use them when they are opened in ddx
        metricsDdxQuery: queryWithGlobalVariables ? queryWithGlobalVariables : undefined,
      },
    };
  };

  // Preallocate the series array for performance
  const series: TimeSeries[] = Array(result.length);
  for (let i = 0; i < series.length; i++) {
    const value = result[i];
    if (!value) continue;
    const seriesConfig = getSeriesConfig?.(value);
    const config = seriesConfig ? merge({}, defaultSeriesConfig(), seriesConfig) : defaultSeriesConfig();
    const { name, formattedName, metric, parsedValues } = parsePrometheusTimeSeries({
      series: value,
      alias,
      query,
      seriesName,
      seriesNameFormat,
    });
    series[i] = {
      name,
      values: parsedValues,
      formattedName,
      labels: metric,
      config,
    };
  }

  return series;
}

/**
 * Returns formatted series name, labels, and parses value tuples
 */
export function parsePrometheusTimeSeries({
  series,
  query,
  alias,
  seriesName,
  seriesNameFormat,
}: ParsePrometheusTimeSeriesOpts) {
  const { metric, values: maybeValues, histograms } = series || { metric: {} };
  const values = maybeValues || [];

  // TODO: We don't support histograms yet
  if (histograms) {
    throw new Error(PROM_HISTOGRAM_ERROR_MESSAGE);
  }

  // Account for series_name_format from query editor when determining name to show in legend, tooltip, etc.
  const { name, formattedName } = getFormattedPrometheusSeriesName({
    query,
    alias,
    seriesName,
    seriesNameFormat,
    metric,
  });

  // Preallocate values array for performance
  const parsedValues: ParsedValueTuple[] = new Array(values.length);

  for (let j = 0; j < parsedValues.length; j++) {
    parsedValues[j] = parseValueTuple(values[j] as ValueTuple);
  }

  return { name, formattedName, metric, parsedValues };
}

/*
 * Transforms Prometheus query result to tabular data frame
 */
export function transformQueryResultToTabularDataFrame({
  result,
  spec,
  compareResult,
  compareDurationInMs,
  alias,
}: {
  result: MatrixData['result'];
  spec: PrometheusQuerySpec;
  compareResult?: MatrixData['result'];
  compareDurationInMs?: number;
  alias?: string;
}): DataFrame {
  if (!result || result.length === 0) {
    return {
      length: 0,
      fields: [],
    };
  }

  // Build a field for every label
  const labels = Object.keys(result.reduce((acc, series) => ({ ...acc, ...series.metric }), {})).sort();
  // Each label field represents the column for a metric label
  const labelFields: Field[] = labels.map((label) => {
    const field: Field = {
      name: label,
      type: FieldType.string,
      values: [],
    };
    return field;
  });

  const metricFieldName = alias ? alias : DATAFRAME_METRIC_FIELD_NAME;
  // Build a field for the metric value
  const metricField: Field = {
    name: metricFieldName,
    type: FieldType.number,
    values: [],
  };

  const seriesToIndex: Record<string, number> = {};

  // Build a row for every series in the query result
  for (let i = 0; i < result.length; i++) {
    const series = result[i];
    if (!series) continue;
    const {
      name,
      metric: labels,
      parsedValues,
    } = parsePrometheusTimeSeries({
      series,
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
    });
    // For each label field, retrieve the corresponding label value and add it to the labelField's values array
    labelFields.forEach((labelField) => labelField.values.push(getLabelValue(labels, labelField.name)));

    const value = getValueForDataFrame(spec, parsedValues);

    metricField.values.push(value);
    seriesToIndex[name] = metricField.values.length - 1;
  }

  const fields: Field[] = [...labelFields];

  if (compareResult && compareResult.length && compareDurationInMs) {
    const name = `${metricFieldName} (${formatMsDuration(compareDurationInMs)} ago)`;
    const compareField: Field = {
      name,
      type: FieldType.number,
      values: Array(metricField.values.length),
    };

    for (let i = 0; i < compareResult.length; i++) {
      const offsetSeries = compareResult[i];
      if (!offsetSeries) continue;
      const { name, parsedValues } = parsePrometheusTimeSeries({
        series: offsetSeries,
        alias,
        query: spec.query,
        seriesName: spec.series_name,
        seriesNameFormat: spec.series_name_format,
      });
      const compareToIndex = seriesToIndex[name];
      if (compareToIndex === undefined || compareToIndex === null) continue;
      const value = getValueForDataFrame(spec, parsedValues);
      compareField.values[compareToIndex] = value;
    }

    fields.push(compareField);
  }

  // Value field always comes last; if there's a comparison, we want to show compare field, then show value field.
  fields.push(metricField);

  return {
    length: metricField.values.length,
    fields,
  };
}

const getValueForDataFrame = (spec: PrometheusQuerySpec, parsedValues: ParsedValueTuple[]) => {
  let value;
  // Here we do an instant query and convert response to a range query response
  // so take the the first value in parsedValues
  const parsedValueTuple = parsedValues[0];
  if (parsedValueTuple) {
    value = parsedValueTuple[1];
  }

  // Reduce to a single value if it is a range query
  if (spec.queryType === 'Range') {
    const reducer = ReducersMap[spec.reducer ?? 'LastNumber'];
    value = reducer(parsedValues) as number;
  }
  return value;
};

export function getSingleValue(
  values: ParsedValueTuple[],
  spec: PrometheusQuerySpec,
  opts?: { alignedEndMs?: number }
) {
  let value: number | null = null;
  // Reduce to a single value if it is a range query
  if (spec.queryType === 'Range') {
    const reducerKey = spec.reducer ?? 'LastNumber';
    // If the final aligned timestamp has no sample, convert to null
    if (reducerKey === 'Last' && opts?.alignedEndMs !== undefined) {
      const lastTuple = values[values.length - 1];
      if (!lastTuple) {
        return null;
      }
      const [lastTs, lastVal] = lastTuple;
      if (lastTs < opts.alignedEndMs) {
        return null;
      }
      return lastVal;
    }
    const reducer = ReducersMap[reducerKey];
    value = reducer(values) as number;
  } else {
    // We should always want the last value in the time series
    const lastIndex = values.length - 1;
    if (!values[lastIndex]) {
      return;
    }
    const [_, lastValue] = values[lastIndex]!;
    value = lastValue;
  }
  return value;
}

/**
 * Transforms Prometheus time series result into single value data
 */
interface SingleValueArgs {
  result: MatrixData['result'];
  spec: PrometheusQuerySpec;
  compareResult?: MatrixData['result'];
  alias?: string;
}

export function transformTimeSeriesToSingleValue({
  result,
  spec,
  compareResult,
  alias,
  alignedEndMs,
}: SingleValueArgs & { alignedEndMs?: number }) {
  const singleValueData: SingleValue[] = [];

  // Build a map of series formatted name and its index for series.compareTo lookup
  const seriesToIndexMap: Record<string, number> = {};

  for (let i = 0; i < result.length; i++) {
    const series = result[i];
    if (!series) continue;
    const { name, formattedName, metric, parsedValues } = parsePrometheusTimeSeries({
      series,
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
    });
    seriesToIndexMap[formattedName] = i;

    const value: number | null | undefined = getSingleValue(parsedValues, spec, {
      alignedEndMs,
    });
    if (value === undefined) {
      continue;
    }

    singleValueData.push({
      name,
      formattedName,
      labels: metric,
      value,
    });
  }

  if (compareResult) {
    for (let i = 0; i < compareResult.length; i++) {
      const offsetSeries = compareResult[i];
      if (!offsetSeries) continue;
      const { name, formattedName, metric, parsedValues } = parsePrometheusTimeSeries({
        series: offsetSeries,
        alias,
        query: spec.query,
        seriesName: spec.series_name,
        seriesNameFormat: spec.series_name_format,
      });

      // Get the index of the series it should be compared to
      const compareToIndex = seriesToIndexMap[formattedName];
      const compareTo = compareToIndex ?? null; // Set to null when the series it should be compared to is missing

      // Here we do an instant query and convert response to a range query response
      // so take the the first value in parsedValues
      if (!parsedValues[0]) continue;
      const [_, value] = parsedValues[0];

      singleValueData.push({
        name: `${name}-offset`,
        formattedName: `${formattedName}-offset`,
        labels: metric,
        value,
        config: { compareTo },
      });
    }
  }

  return singleValueData;
}

/**
 * Transforms Prometheus time series result into single value time series data
 */
interface SingleValueTimeSeriesArgs {
  result: MatrixData['result'];
  timeSeriesResult: MatrixData['result'];
  spec: PrometheusQuerySpec;
  compareResult?: MatrixData['result'];
  alias?: string;
}
export function transformTimeSeriesToSingleValueTimeSeries({
  result,
  timeSeriesResult,
  spec,
  compareResult,
  alias,
  alignedEndMs,
}: SingleValueTimeSeriesArgs & { alignedEndMs?: number }) {
  const data: SingleValueTimeSeries[] = [];

  // Build a map of non-offset series formatted name and its index for series.compareTo lookup
  const seriesToIndexMap: Record<string, number> = {};
  const timeSeriesToIndexMap: Record<string, number> = {};

  // Parse the time series result and build the timeSeriesToIndexMap
  const parsedTimeSeriesResult = timeSeriesResult.map((result, index) => {
    const { metric, parsedValues } = parsePrometheusTimeSeries({
      series: result,
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
    });
    // If compare mode is enabled, the time series result includes both non-offset series and their corresponding compareable series
    // Remove "__chrono_series__" and "__chrono_offset__" labels if they exist
    const labels = produce(metric, (draft: typeof metric) => {
      delete draft[SeriesIdLabel];
      delete draft[OffsetLabel];
    });

    let seriesName = getFormattedPrometheusSeriesName({
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
      metric: labels,
    });

    const isOffsetSeries = result.metric !== undefined && result.metric[OffsetLabel] !== undefined;
    if (isOffsetSeries) {
      // Append "-offset" to the series name
      seriesName = {
        name: `${seriesName.name}-offset`,
        formattedName: `${seriesName.formattedName}-offset`,
      };
    }

    timeSeriesToIndexMap[seriesName.formattedName] = index;

    return {
      ...seriesName,
      metric,
      values: parsedValues,
    };
  });

  for (let i = 0; i < result.length; i++) {
    const series = result[i];
    if (!series) continue;

    const { name, formattedName, metric, parsedValues } = parsePrometheusTimeSeries({
      series,
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
    });

    const value: number | null | undefined = getSingleValue(parsedValues, spec, {
      alignedEndMs,
    });
    if (value === undefined) {
      continue;
    }

    seriesToIndexMap[formattedName] = i;

    const timeSeriesIndex = timeSeriesToIndexMap[formattedName];
    let timeSeriesValues;
    if (timeSeriesIndex !== undefined) {
      const series = parsedTimeSeriesResult[timeSeriesIndex];
      timeSeriesValues = series?.values;
    }

    data.push({
      name,
      formattedName,
      labels: metric,
      value,
      timeSeries: timeSeriesValues ?? [],
    });
  }

  if (compareResult) {
    for (let i = 0; i < compareResult.length; i++) {
      const offsetSeries = compareResult[i];
      if (!offsetSeries) continue;

      const { name, formattedName, metric, parsedValues } = parsePrometheusTimeSeries({
        series: offsetSeries,
        alias,
        query: spec.query,
        seriesName: spec.series_name,
        seriesNameFormat: spec.series_name_format,
      });
      // Here we do an instant query and convert response to a range query response
      // so take the the first value in parsedValues
      if (!parsedValues[0]) continue;
      const [_, value] = parsedValues[0];

      // Get the index of the series it should be compared to
      const compareToIndex = seriesToIndexMap[formattedName];
      const compareTo = compareToIndex ?? null; // Set to null when the series it should be compared to is missing

      let timeSeriesValues;
      const timeSeriesIndex = timeSeriesToIndexMap[`${formattedName}-offset`];
      if (timeSeriesIndex !== undefined) {
        const series = parsedTimeSeriesResult[timeSeriesIndex];
        timeSeriesValues = series?.values;
      }

      data.push({
        name: `${name}-offset`,
        formattedName: `${formattedName}-offset`,
        labels: metric,
        value,
        timeSeries: timeSeriesValues ?? [],
        config: { compareTo },
      });
    }
  }

  return data;
}

// Error messages for histogram types
export const NATIVE_HISTOGRAM_ERROR_MESSAGE = 'Queries resulted in exponential histograms. Consider using a heat map.';
export const TIMESERIES_HISTOGRAM_ERROR_MESSAGE =
  'Queries resulted in histograms. Consider calculating aggregated quantile by adding histogram_quantile() or using a heatmap.';
